import React, { useState, useEffect } from 'react';
import { useParams, Link as RouterLink } from 'react-router-dom';
import { Container, Text, Title, Paper, Button, Loader, Group } from '@mantine/core';
import axios from 'axios';
import { useTranslation } from 'react-i18next';
import { motion } from 'framer-motion';

const API_BASE_URL = window.location.hostname === 'alt-service.lingva-pro.org'
  ? window.location.origin + '/api'
  : (process.env.REACT_APP_API_BASE_URL || 'http://localhost:3001/api');
const LOCAL_STORAGE_PATH = '/local_files';

function GuidePageManual() {
  const { articleId, userId, teacherId } = useParams();
  const [article, setArticle] = useState(null);
  const [loading, setLoading] = useState(true);
  const { t } = useTranslation();

  const ensureFilesExist = async (body) => {
    const fileIds = [...body.matchAll(/src="\?entryPoint=attachment&amp;id=([^"]+)"/g)].map(match => match[1]);
    for (const fileId of fileIds) {
      const localFilePath = `${LOCAL_STORAGE_PATH}/${fileId}`;
      try {
        const response = await fetch(localFilePath, { method: 'HEAD' });
        if (!response.ok) {
          const fileResponse = await axios.get(`${API_BASE_URL}/download/${fileId}`, { responseType: 'blob' });
          const blob = new Blob([fileResponse.data]);
          await fetch(localFilePath, { method: 'PUT', body: blob });
        }
      } catch (error) {
        console.error(t('fileProcessingError', { fileId }), error.message);
      }
    }
  };

  const processBody = async (body) => {
    if (!body) return '';
    await ensureFilesExist(body);
    return body
      .replace(/src="\?entryPoint=attachment&amp;id=([^"]+)"/g, (match, id) => {
        return `src="${LOCAL_STORAGE_PATH}/${id}"`;
      })
      .replace(/<img[^>]*>/g, (imgTag) => {
        return imgTag.replace(/style="[^"]*"|width="[^"]*"/g, '').replace('<img', '<img style="max-width: 800px; width: 100%; height: auto;"');
      });
  };

  useEffect(() => {
    const fetchAndProcessArticle = async () => {
      try {
        const response = await fetch(`${API_BASE_URL}/knowledgeBaseArticle/${articleId}`);
        if (!response.ok) {
          throw new Error(`Ошибка HTTP: ${response.status}`);
        }
        const data = await response.json();
        data.body = await processBody(data.body);
        setArticle(data);
      } catch (error) {
        console.error(t('articleLoadingError'), error.message);
      } finally {
        setLoading(false);
      }
    };

    fetchAndProcessArticle();
  }, [articleId]);

  return (
    <div style={{ backgroundColor: '#f0f9ff', minHeight: '100vh' }}>
      {/* Header */}
      <header style={{
        background: 'white',
        boxShadow: '0 2px 8px rgba(0,0,0,0.06)',
        position: 'sticky',
        top: 0,
        zIndex: 100,
      }}>
        <Container size="xl">
          <Group h={64} justify="space-between">
            <RouterLink
              to={teacherId ? `/teacher/${teacherId}/` : `/${userId}/`}
              style={{ display: 'flex', alignItems: 'center' }}
            >
              <img src="/logo.png" alt="LingvaPro Logo" style={{ height: 40 }} />
            </RouterLink>
            <Button
              variant="subtle"
              color="dark"
              component={RouterLink}
              to={teacherId ? `/teacher/${teacherId}/guide` : `/${userId}/guide`}
            >
              {t('returnToMain')}
            </Button>
          </Group>
        </Container>
      </header>

      <div style={{ padding: 20, maxWidth: 900, margin: '0 auto' }}>
        {loading ? (
          <div style={{ display: 'flex', justifyContent: 'center', alignItems: 'center', height: '50vh' }}>
            <Loader size="lg" color="var(--primary)" />
          </div>
        ) : article ? (
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.5 }}
          >
            <Title order={2} fw={700} mb="lg" ta="left">
              {article.name}
            </Title>
            <Paper
              p="lg"
              radius="xl"
              shadow="sm"
              style={{
                whiteSpace: 'pre-line',
                backgroundColor: 'white',
              }}
              dangerouslySetInnerHTML={{ __html: article.body }}
            />
          </motion.div>
        ) : (
          <Text ta="center" c="var(--text-muted)" mt="lg">
            {t('articleNotFound')}
          </Text>
        )}
      </div>
    </div>
  );
}

export default GuidePageManual;
